import { useEffect, useMemo, useState } from "react";
import {
    Plus,
    X,
    Trash2,
    Network,
    Users,
    Sparkles,
    Briefcase,
    TrendingUp,
    Phone,
    Calendar,
    Handshake,
    ArrowRight,
    Activity,
    Utensils,
    Mail,
    Presentation,
    Tag,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";
import { Field, Select } from "./CockpitDossiers";

// ─────────────────────────────────────────────────────────────
// Réseau XV — relational cockpit (formerly "Partenaires").
// Combines: directory + overview + recent activity + per-partner
// interlocutor management (multiple people per structure).
// ─────────────────────────────────────────────────────────────

const TYPES = [
    { v: "family_office", l: "Family Office" },
    { v: "banque_privee", l: "Banque privée" },
    { v: "avocat", l: "Avocat" },
    { v: "fiscaliste", l: "Fiscaliste" },
    { v: "expert_comptable", l: "Expert-comptable" },
    { v: "notaire", l: "Notaire" },
    { v: "investisseur_prive", l: "Investisseur privé" },
    { v: "dirigeant", l: "Dirigeant" },
    { v: "institutionnel", l: "Institutionnel" },
    { v: "relation_strategique", l: "Relation stratégique" },
];

// Legacy values still kept readable
const LEGACY_TYPES = {
    entrepreneur: "Dirigeant",
    immobilier: "Relation stratégique",
    private_debt: "Investisseur privé",
    corporate: "Institutionnel",
    venture: "Investisseur privé",
    autre: "Relation stratégique",
};

const TEMP = [
    { v: "cold", l: "Froide" },
    { v: "warm", l: "Tiède" },
    { v: "hot", l: "Chaude" },
    { v: "alumni", l: "Historique" },
];

const ROLES = [
    { v: "", l: "—" },
    { v: "decisionnaire", l: "Décisionnaire" },
    { v: "apporteur", l: "Apporteur d'affaires" },
    { v: "operationnel", l: "Opérationnel" },
    { v: "expert", l: "Expert" },
];

const INTERACTION_LABELS = {
    appel: "Appel",
    rdv: "Rendez-vous",
    dejeuner: "Déjeuner",
    email: "Email",
    conference: "Conférence",
    introduction: "Introduction",
    recommandation: "Recommandation",
    mise_en_relation: "Mise en relation",
};
const INTERACTION_ICONS = {
    appel: Phone,
    rdv: Calendar,
    dejeuner: Utensils,
    email: Mail,
    conference: Presentation,
    introduction: Handshake,
    recommandation: Sparkles,
    mise_en_relation: ArrowRight,
};

const OPPORTUNITY_TAGS = [
    "Immobilier",
    "Assurance",
    "Patrimonial",
    "Fiscal",
    "Comptable",
    "Juridique",
    "M&A",
    "Financement",
    "International",
    "Private Equity",
];

function typeLabel(v) {
    return (
        TYPES.find((t) => t.v === v)?.l
        || LEGACY_TYPES[v]
        || v
        || "Non classé"
    );
}
function tempColor(t) {
    return {
        cold: "text-[#9bb4d6]",
        warm: "text-[#d4af37]/90",
        hot: "text-[#e6a26b]",
        alumni: "text-[#f4ecd8]/55",
    }[t] || "text-[#f4ecd8]/55";
}

const EMPTY = {
    name: "",
    type: "family_office",
    geographies: [],
    sectors: [],
    ticket_band: "",
    risk_profile: "",
    instrument: "",
    relationship_temp: "cold",
    trust_level: 3,
    last_contact: "",
    notes: "",
    history: "",
    opportunity_tags: [],
    interlocutors: [],
};

function fmtMoney(n) {
    const v = Number(n) || 0;
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)} M€`;
    if (v >= 1000) return `${Math.round(v / 1000)} k€`;
    return `${v} €`;
}

export default function CockpitPartners() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("");
    const [groupBy, setGroupBy] = useState("type");
    const [overview, setOverview] = useState(null);
    const [activity, setActivity] = useState([]);

    const load = async () => {
        const [parts, ov, act] = await Promise.all([
            cockpit.get("/network/partners").then((r) => r.data),
            cockpit.get("/network/overview").then((r) => r.data).catch(() => null),
            cockpit
                .get("/network/recent-activity")
                .then((r) => r.data?.items || [])
                .catch(() => []),
        ]);
        setItems(parts);
        setOverview(ov);
        setActivity(act);
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = items.filter((p) => {
        if (!filter) return true;
        const q = filter.toLowerCase();
        const hay = [
            p.name,
            p.type,
            ...(p.geographies || []),
            ...(p.sectors || []),
            ...(p.interlocutors || []).flatMap((i) => [
                i.first_name,
                i.last_name,
                i.role_title,
                i.company,
                i.email,
            ]),
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return hay.includes(q);
    });

    return (
        <div
            data-testid="cockpit-network"
            className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14"
        >
            {/* HEADER */}
            <div className="flex flex-wrap items-end justify-between mb-12 gap-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Mémoire relationnelle · Confidentiel
                    </div>
                    <h1
                        className="font-serif text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.08] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Réseau XV.
                    </h1>
                    <p className="mt-3 text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                        Cockpit relationnel. Chaque structure rassemble ses
                        interlocuteurs, leurs contributions et l'historique
                        des échanges. Un carnet d'adresses devenu actif
                        pilotable.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="cockpit-new-partner"
                    onClick={() => setEditing({ ...EMPTY })}
                    className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                >
                    <Plus strokeWidth={1.2} size={14} />
                    Nouvelle structure
                </button>
            </div>

            {/* OVERVIEW + CARTOGRAPHY */}
            {overview && (
                <>
                    <NetworkingSuggestionsBlock />
                    <NetworkTopBanner overview={overview} />
                    <NetworkOverviewBlock overview={overview} />
                </>
            )}

            <div className="grid lg:grid-cols-12 gap-12 mb-14">
                {/* DIRECTORY */}
                <div className="lg:col-span-8">
                    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                        <div className="overline text-[#d4af37]/70">
                            Répertoire des structures · {filtered.length}
                        </div>
                        <div className="flex gap-3 items-center flex-wrap">
                            <div className="inline-flex border border-white/15">
                                {[
                                    { v: "type", l: "Par type" },
                                    { v: "geography", l: "Géographie" },
                                    { v: "none", l: "Liste" },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        data-testid={`partners-group-${opt.v}`}
                                        onClick={() => setGroupBy(opt.v)}
                                        className={`px-3.5 py-1.5 text-[10px] tracking-[0.26em] uppercase transition-all duration-500 ${
                                            groupBy === opt.v
                                                ? "bg-[#d4af37]/15 text-[#d4af37]"
                                                : "text-[#f4ecd8]/55 hover:text-[#f4ecd8]"
                                        }`}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                            <input
                                type="text"
                                placeholder="Filtrer…"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none px-3 py-1.5 text-[13px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/30 w-44"
                            />
                        </div>
                    </div>
                    <div className="border-t border-white/[0.06]">
                        {filtered.length === 0 ? (
                            <div className="py-20 text-center">
                                <Network
                                    strokeWidth={0.8}
                                    size={42}
                                    className="text-[#d4af37]/40 mx-auto mb-6"
                                />
                                <p className="text-[#f4ecd8]/50 text-[14px]">
                                    Aucune structure enregistrée.
                                </p>
                            </div>
                        ) : groupBy === "none" ? (
                            filtered.map((p) => (
                                <PartnerRow
                                    key={p.id}
                                    p={p}
                                    onClick={() => setEditing(p)}
                                />
                            ))
                        ) : (
                            <GroupedPartners
                                items={filtered}
                                groupBy={groupBy}
                                onSelect={(p) => setEditing(p)}
                            />
                        )}
                    </div>
                </div>

                {/* RECENT ACTIVITY */}
                <aside className="lg:col-span-4">
                    <RecentActivityBlock items={activity} />
                </aside>
            </div>

            {editing && (
                <PartnerEditor
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// NetworkingSuggestionsBlock — actionable nudges on top of Réseau XV.
// Heuristic suggestions: revive dormant strong ties, first contacts,
// deepen hot active relationships.
// ─────────────────────────────────────────────────────────────
function NetworkingSuggestionsBlock() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        cockpit
            .get("/network/networking-suggestions")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]));
    }, []);
    if (items.length === 0) return null;

    const KIND_MAP = {
        revive: { label: "Relance", icon: Phone },
        deepen: { label: "Approfondir", icon: Sparkles },
        first_contact: { label: "Premier contact", icon: Handshake },
        event: { label: "Événement", icon: Presentation },
        introduction: { label: "Introduction", icon: ArrowRight },
    };

    return (
        <section
            data-testid="networking-suggestions"
            className="mb-10 bg-[#070d18] border border-[#d4af37]/25 p-8 lg:p-10"
        >
            <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <div className="overline text-[#d4af37]/85 mb-2">
                        <Sparkles
                            strokeWidth={1}
                            size={12}
                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                        />
                        Réseautage
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Suggestions de rencontres & activités
                    </h2>
                </div>
                <p className="text-[11.5px] text-[#f4ecd8]/40 italic font-light max-w-md leading-relaxed">
                    Lecture automatique de l'état du Réseau XV — relances,
                    rencontres à approfondir, premiers contacts à ouvrir.
                </p>
            </div>
            <ul className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((s, i) => {
                    const meta = KIND_MAP[s.kind] || KIND_MAP.deepen;
                    const Icon = meta.icon;
                    return (
                        <li
                            key={i}
                            data-testid={`networking-${s.kind}-${i}`}
                            className="border border-white/[0.07] p-5 hover:border-[#d4af37]/40 transition-all duration-500"
                        >
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/85">
                                    <Icon strokeWidth={1.2} size={12} />
                                    {meta.label}
                                </div>
                                {s.activity && (
                                    <span className="text-[9.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/50 border-l border-white/15 pl-2">
                                        {s.activity}
                                    </span>
                                )}
                            </div>
                            <div
                                className="font-serif text-[16px] text-[#f4ecd8] leading-tight mb-2"
                                style={{ fontWeight: 400 }}
                            >
                                {s.title}
                            </div>
                            <div className="text-[11px] text-[#f4ecd8]/45 mb-3 leading-relaxed">
                                {s.subtitle}
                            </div>
                            <p className="text-[12.5px] font-serif text-[#f4ecd8]/80 italic leading-relaxed">
                                « {s.suggestion} »
                            </p>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// NetworkTopBanner — Two prominent metrics: dossiers issus du réseau + CA réseau
// ─────────────────────────────────────────────────────────────
function NetworkTopBanner({ overview }) {
    const c = overview.cartography || {};
    return (
        <section
            data-testid="network-top-banner"
            className="mb-10 grid sm:grid-cols-2 gap-px bg-[#d4af37]/15 border border-[#d4af37]/30"
        >
            <div className="bg-[#0b1220] p-8 lg:p-10">
                <div className="overline text-[#d4af37]/85 mb-3">
                    Dossiers issus du réseau
                </div>
                <div
                    className="font-serif text-[58px] sm:text-[68px] text-[#f4ecd8] leading-none"
                    style={{ fontWeight: 300 }}
                >
                    {c.dossiers_from_network ?? 0}
                </div>
                <p className="mt-4 text-[12px] tracking-[0.24em] uppercase text-[#d4af37]/65">
                    Missions trouvant leur origine dans le Réseau XV
                </p>
            </div>
            <div className="bg-[#0b1220] p-8 lg:p-10">
                <div className="overline text-[#d4af37]/85 mb-3">
                    Chiffre d'affaires généré
                </div>
                <div
                    className="font-serif text-[58px] sm:text-[68px] text-[#f4ecd8] leading-none"
                    style={{ fontWeight: 300 }}
                >
                    {fmtMoney(c.revenue_from_network)}
                </div>
                <p className="mt-4 text-[12px] tracking-[0.24em] uppercase text-[#d4af37]/65">
                    Honoraires perçus grâce aux recommandations
                </p>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// NetworkOverviewBlock — counts + breakdown + cartography
// ─────────────────────────────────────────────────────────────
function NetworkOverviewBlock({ overview }) {
    const byType = (overview.by_type || []).filter((t) => t.count > 0);
    const c = overview.cartography || {};

    return (
        <section
            data-testid="network-overview"
            className="mb-14 bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10"
        >
            <div className="grid lg:grid-cols-12 gap-10">
                {/* Total + breakdown */}
                <div className="lg:col-span-5">
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Vue d'ensemble
                    </div>
                    <div
                        className="font-serif text-[54px] text-[#f4ecd8] leading-none"
                        style={{ fontWeight: 300 }}
                    >
                        {overview.total}
                    </div>
                    <p className="text-[11px] tracking-[0.28em] uppercase text-[#d4af37]/65 mt-3">
                        Relations qualifiées au répertoire
                    </p>

                    <div className="mt-9 space-y-2">
                        {byType.length === 0 && (
                            <p className="text-[12px] text-[#f4ecd8]/40 italic font-light">
                                Le répertoire est encore vide.
                            </p>
                        )}
                        {byType.map((t) => (
                            <div
                                key={t.value}
                                data-testid={`network-type-${t.value}`}
                                className="flex items-baseline justify-between border-b border-white/[0.05] py-2"
                            >
                                <span className="text-[13px] font-serif text-[#f4ecd8]/85">
                                    {t.label}
                                </span>
                                <span className="text-[11px] tracking-[0.22em] uppercase text-[#d4af37]/85">
                                    {t.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Cartography metrics */}
                <div className="lg:col-span-7">
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Cartographie du réseau
                    </div>
                    <h2
                        className="font-serif text-[20px] text-[#f4ecd8] mb-7 leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Synthèse de l'actif relationnel
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
                        <Stat
                            label="Qualifiées"
                            value={c.qualified}
                            sub="Confiance ≥ 4"
                        />
                        <Stat
                            label="Actives"
                            value={c.active}
                            sub="Échange < 90 j"
                        />
                        <Stat
                            label="Chaudes"
                            value={c.hot}
                            sub="Temp. relationnelle"
                        />
                        <Stat
                            label="Dossiers réseau"
                            value={c.dossiers_from_network}
                            sub="Issus du réseau"
                        />
                        <Stat
                            label="CA réseau"
                            value={fmtMoney(c.revenue_from_network)}
                            sub="Recommandations"
                        />
                        <Stat
                            label="Couverture"
                            value={
                                overview.total > 0
                                    ? `${Math.round((c.active / overview.total) * 100)} %`
                                    : "—"
                            }
                            sub="Réseau actif"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value, sub }) {
    return (
        <div className="bg-[#070d18] p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/45 mb-2">
                {label}
            </div>
            <div
                className="font-serif text-[28px] text-[#f4ecd8] leading-none"
                style={{ fontWeight: 300 }}
            >
                {value ?? "—"}
            </div>
            {sub && (
                <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55">
                    {sub}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// RecentActivityBlock — last 10 interactions across the network
// ─────────────────────────────────────────────────────────────
function RecentActivityBlock({ items }) {
    const top = items.slice(0, 10);
    return (
        <div data-testid="network-recent-activity" className="bg-white/[0.02] border border-white/[0.06] p-8">
            <div className="overline text-[#d4af37]/70 mb-3">
                <Activity
                    strokeWidth={1}
                    size={12}
                    className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                />
                Activité récente
            </div>
            <h3
                className="font-serif text-[18px] text-[#f4ecd8] mb-7 leading-tight"
                style={{ fontWeight: 400 }}
            >
                Mémoire des échanges
            </h3>
            {top.length === 0 && (
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light">
                    Aucune interaction tracée. Commencez par enregistrer un
                    appel, un RDV ou une introduction depuis la fiche
                    d'un interlocuteur.
                </p>
            )}
            <ul className="space-y-4">
                {top.map((a) => {
                    const Icon = INTERACTION_ICONS[a.kind] || Activity;
                    return (
                        <li
                            key={a.id}
                            data-testid={`activity-${a.id}`}
                            className="flex items-start gap-3 py-2 border-b border-white/[0.04] last:border-b-0"
                        >
                            <Icon
                                strokeWidth={1.1}
                                size={13}
                                className="text-[#d4af37] mt-1 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-[12.5px] font-serif text-[#f4ecd8]/90 leading-tight truncate">
                                    {a.interlocutor_name || "—"}{" "}
                                    <span className="text-[#f4ecd8]/40">
                                        · {a.partner_name}
                                    </span>
                                </div>
                                <div className="mt-1.5 flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase">
                                    <span className="text-[#d4af37]/75">
                                        {INTERACTION_LABELS[a.kind] || a.kind}
                                    </span>
                                    <span className="text-[#f4ecd8]/40">
                                        {a.date}
                                    </span>
                                </div>
                                {a.note && (
                                    <p className="mt-1.5 text-[11.5px] font-light text-[#f4ecd8]/55 italic leading-relaxed truncate">
                                        {a.note}
                                    </p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// PartnerRow / GroupedPartners — directory items
// ─────────────────────────────────────────────────────────────

function PartnerRow({ p, onClick }) {
    const interCount = p.interlocutors_count ?? (p.interlocutors || []).length;
    const tags = (p.opportunity_tags || []).slice(0, 4);
    const lastInt = p.last_interaction || "";
    return (
        <button
            type="button"
            data-testid={`partner-row-${p.id}`}
            onClick={onClick}
            className="w-full block py-5 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors duration-500 px-2 text-left"
        >
            <div className="grid grid-cols-[1fr_auto] items-start gap-6">
                <div className="min-w-0">
                    <div
                        className="font-serif text-[19px] text-[#f4ecd8] leading-tight truncate"
                        style={{ fontWeight: 400 }}
                    >
                        {p.name}
                    </div>
                    <div className="mt-1 text-[12px] text-[#f4ecd8]/45 tracking-wide truncate">
                        {typeLabel(p.type)}
                        {p.geographies?.length
                            ? ` · ${p.geographies.join(", ")}`
                            : ""}
                    </div>
                    {tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {tags.map((t) => (
                                <span
                                    key={t}
                                    className="text-[9.5px] tracking-[0.18em] uppercase border border-[#d4af37]/25 text-[#d4af37]/80 px-2 py-0.5"
                                >
                                    {t}
                                </span>
                            ))}
                            {(p.opportunity_tags || []).length > 4 && (
                                <span className="text-[9.5px] tracking-[0.18em] uppercase text-[#f4ecd8]/40 py-0.5">
                                    +{(p.opportunity_tags || []).length - 4}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <span
                    className={`text-[10px] tracking-[0.26em] uppercase ${tempColor(
                        p.relationship_temp,
                    )} shrink-0`}
                >
                    {TEMP.find((t) => t.v === p.relationship_temp)?.l}
                </span>
            </div>

            <div className="mt-3 grid grid-cols-5 gap-3 text-center">
                <RowStat
                    label="Inter."
                    value={interCount}
                    icon={Users}
                />
                <RowStat
                    label="Dossiers"
                    value={p.dossiers_count ?? 0}
                />
                <RowStat
                    label="Intro."
                    value={p.introductions_count ?? 0}
                />
                <RowStat
                    label="Missions"
                    value={p.missions_count ?? 0}
                />
                <RowStat
                    label="Dernier"
                    value={lastInt || "—"}
                    small
                />
            </div>
        </button>
    );
}

function RowStat({ label, value, icon: Icon, small }) {
    return (
        <div>
            <div className="text-[9px] tracking-[0.24em] uppercase text-[#f4ecd8]/40 mb-1 flex items-center justify-center gap-1">
                {Icon && (
                    <Icon
                        strokeWidth={1.1}
                        size={9}
                        className="text-[#d4af37]/70"
                    />
                )}
                {label}
            </div>
            <div
                className={`font-serif text-[#f4ecd8] leading-none ${
                    small ? "text-[12px]" : "text-[18px]"
                }`}
                style={{ fontWeight: small ? 400 : 300 }}
            >
                {value}
            </div>
        </div>
    );
}

function GroupedPartners({ items, groupBy, onSelect }) {
    const groups = {};
    for (const p of items) {
        if (groupBy === "type") {
            const key = typeLabel(p.type);
            groups[key] = groups[key] || [];
            groups[key].push(p);
        } else {
            const geos = (p.geographies || []).filter(Boolean);
            if (geos.length === 0) {
                groups["Non précisé"] = groups["Non précisé"] || [];
                groups["Non précisé"].push(p);
            } else {
                for (const g of geos) {
                    groups[g] = groups[g] || [];
                    groups[g].push(p);
                }
            }
        }
    }
    const keys = Object.keys(groups).sort(
        (a, b) => groups[b].length - groups[a].length,
    );

    return (
        <div data-testid="partners-grouped">
            {keys.map((k) => (
                <section
                    key={k}
                    data-testid={`partners-group-section-${k}`}
                    className="pt-8 pb-2 first:pt-4"
                >
                    <div className="flex items-baseline justify-between mb-3 pb-2 border-b border-[#d4af37]/15">
                        <h3
                            className="font-serif text-[16px] text-[#f4ecd8] tracking-wide"
                            style={{ fontWeight: 400 }}
                        >
                            {k}
                        </h3>
                        <span className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37]/70">
                            {groups[k].length}
                        </span>
                    </div>
                    {groups[k].map((p) => (
                        <PartnerRow
                            key={`${k}-${p.id}`}
                            p={p}
                            onClick={() => onSelect(p)}
                        />
                    ))}
                </section>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// PartnerEditor — modal with 2 tabs: Identité + Interlocuteurs
// ─────────────────────────────────────────────────────────────

function PartnerEditor({ initial, onClose, onSaved }) {
    const isNew = !initial.id;
    const [tab, setTab] = useState("identity");
    const [partner, setPartner] = useState(initial);
    const [form, setForm] = useState({
        ...initial,
        geographies: (initial.geographies || []).join(", "),
        sectors: (initial.sectors || []).join(", "),
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = {
                name: form.name,
                type: form.type,
                geographies: form.geographies
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                sectors: form.sectors
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                ticket_band: form.ticket_band,
                risk_profile: form.risk_profile,
                instrument: form.instrument,
                relationship_temp: form.relationship_temp,
                trust_level: Number(form.trust_level) || 3,
                last_contact: form.last_contact,
                notes: form.notes,
                history: form.history,
                opportunity_tags: form.opportunity_tags || [],
            };
            let updated;
            if (isNew) {
                ({ data: updated } = await cockpit.post("/partners", payload));
            } else {
                ({ data: updated } = await cockpit.put(
                    `/partners/${initial.id}`,
                    payload,
                ));
            }
            setPartner(updated);
            if (isNew) {
                setTab("interlocutors");
            } else {
                onSaved();
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer "${initial.name}" du réseau ?`)) return;
        await cockpit.delete(`/partners/${initial.id}`);
        onSaved();
    };

    return (
        <div
            data-testid="partner-modal"
            className="fixed inset-0 z-50 bg-[#070d18]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[860px] mt-8 mb-20 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500 z-10"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>

                <div className="px-10 lg:px-14 pt-10 pb-6 border-b border-white/[0.06]">
                    <div className="overline text-[#d4af37]/80 mb-3">
                        {isNew ? "Nouvelle structure" : "Structure"}
                    </div>
                    <h2
                        className="font-serif text-[26px] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        {isNew ? "Ajouter au réseau." : partner.name}
                    </h2>

                    {!isNew && (
                        <div className="mt-6 inline-flex border border-white/15">
                            {[
                                { v: "identity", l: "Identité" },
                                {
                                    v: "interlocutors",
                                    l: `Interlocuteurs · ${(partner.interlocutors || []).length}`,
                                },
                            ].map((t) => (
                                <button
                                    key={t.v}
                                    type="button"
                                    onClick={() => setTab(t.v)}
                                    data-testid={`partner-tab-${t.v}`}
                                    className={`px-4 py-2 text-[10px] tracking-[0.26em] uppercase transition-all duration-500 ${
                                        tab === t.v
                                            ? "bg-[#d4af37]/15 text-[#d4af37]"
                                            : "text-[#f4ecd8]/55 hover:text-[#f4ecd8]"
                                    }`}
                                >
                                    {t.l}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-10 lg:px-14 py-8">
                    {tab === "identity" ? (
                        <form onSubmit={submit}>
                            <div className="space-y-6">
                                <Field
                                    label="Nom"
                                    required
                                    value={form.name}
                                    onChange={onChange("name")}
                                    testid="partner-name"
                                />
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Select
                                        label="Type"
                                        value={form.type}
                                        onChange={onChange("type")}
                                        options={TYPES}
                                    />
                                    <Field
                                        label="Ticket habituel"
                                        value={form.ticket_band}
                                        onChange={onChange("ticket_band")}
                                    />
                                </div>
                                <Field
                                    label="Zones géographiques (séparées par virgule)"
                                    value={form.geographies}
                                    onChange={onChange("geographies")}
                                />
                                <Field
                                    label="Secteurs préférés (séparés par virgule)"
                                    value={form.sectors}
                                    onChange={onChange("sectors")}
                                />
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Field
                                        label="Profil de risque"
                                        value={form.risk_profile}
                                        onChange={onChange("risk_profile")}
                                    />
                                    <Field
                                        label="Instrument"
                                        value={form.instrument}
                                        onChange={onChange("instrument")}
                                    />
                                </div>
                                <div className="grid sm:grid-cols-3 gap-6">
                                    <Select
                                        label="Température"
                                        value={form.relationship_temp}
                                        onChange={onChange("relationship_temp")}
                                        options={TEMP}
                                    />
                                    <Field
                                        label="Dernier contact"
                                        value={form.last_contact}
                                        onChange={onChange("last_contact")}
                                    />
                                    <div>
                                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                            Confiance (1-5)
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={5}
                                            value={form.trust_level}
                                            onChange={onChange("trust_level")}
                                            className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[15px] font-serif text-[#f4ecd8]"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Historique
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={form.history}
                                        onChange={onChange("history")}
                                        className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                        Notes privées
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={form.notes}
                                        onChange={onChange("notes")}
                                        className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-2">
                                        <Tag
                                            strokeWidth={1}
                                            size={12}
                                            className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                                        />
                                        Opportunités identifiées
                                    </label>
                                    <p className="text-[11px] text-[#f4ecd8]/40 italic font-light mb-3">
                                        Domaines sur lesquels cette structure peut nourrir un dossier XV.
                                    </p>
                                    <div
                                        data-testid="opportunity-tags"
                                        className="flex flex-wrap gap-2"
                                    >
                                        {OPPORTUNITY_TAGS.map((t) => {
                                            const active = (
                                                form.opportunity_tags || []
                                            ).includes(t);
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    data-testid={`tag-${t}`}
                                                    onClick={() => {
                                                        const cur =
                                                            form.opportunity_tags || [];
                                                        const next = active
                                                            ? cur.filter((x) => x !== t)
                                                            : [...cur, t];
                                                        setForm((f) => ({
                                                            ...f,
                                                            opportunity_tags: next,
                                                        }));
                                                    }}
                                                    className={`text-[11px] tracking-[0.16em] px-3 py-1.5 border transition-all duration-500 ${
                                                        active
                                                            ? "border-[#d4af37] bg-[#d4af37]/15 text-[#d4af37]"
                                                            : "border-white/15 text-[#f4ecd8]/60 hover:border-[#d4af37]/40 hover:text-[#f4ecd8]"
                                                    }`}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {err && (
                                <div className="mt-6 text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                                    {err}
                                </div>
                            )}

                            <div className="mt-10 flex items-center justify-between">
                                {!isNew ? (
                                    <button
                                        type="button"
                                        onClick={remove}
                                        className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#e89090]/80 hover:text-[#e89090] transition-colors duration-500"
                                    >
                                        <Trash2 strokeWidth={1.2} size={12} />
                                        Supprimer
                                    </button>
                                ) : (
                                    <span />
                                )}
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                                    >
                                        Fermer
                                    </button>
                                    <button
                                        type="submit"
                                        data-testid="partner-save"
                                        disabled={busy}
                                        className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                                    >
                                        {busy
                                            ? "Enregistrement…"
                                            : isNew
                                              ? "Continuer →"
                                              : "Mettre à jour"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <InterlocutorsTab
                            partner={partner}
                            onUpdated={(p) => setPartner(p)}
                            onClose={onSaved}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// InterlocutorsTab — manage people attached to a structure
// ─────────────────────────────────────────────────────────────
function InterlocutorsTab({ partner, onUpdated, onClose }) {
    const [list, setList] = useState(partner.interlocutors || []);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const persist = async (next) => {
        setBusy(true);
        setErr("");
        try {
            const { data } = await cockpit.put(
                `/partners/${partner.id}/interlocutors`,
                { interlocutors: next },
            );
            setList(data.interlocutors || []);
            onUpdated?.(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    const updateLocal = (id, patch) =>
        setList((prev) =>
            prev.map((i) => (i.id === id ? { ...i, ...patch } : i)),
        );

    const addNew = () => {
        const next = [
            ...list,
            {
                id: `new-${Date.now()}`,
                first_name: "",
                last_name: "",
                role_title: "",
                company: partner.name || "",
                email: "",
                phone: "",
                location: "",
                role: "",
                trust_level: 3,
                last_interaction: "",
                history: "",
                interactions: [],
            },
        ];
        setList(next);
        setExpandedId(next[next.length - 1].id);
    };

    const removeLocal = async (id) => {
        if (!window.confirm("Retirer cet interlocuteur ?")) return;
        const next = list.filter((i) => i.id !== id);
        setList(next);
        await persist(next);
    };

    const saveAll = () => persist(list);

    return (
        <div data-testid="interlocutors-tab">
            <div className="flex items-center justify-between mb-6">
                <p className="text-[12.5px] text-[#f4ecd8]/45 italic font-light max-w-md leading-relaxed">
                    Chaque interlocuteur est rattaché à cette structure. Vous
                    pouvez consigner appels, RDV, introductions et
                    recommandations directement sur sa fiche.
                </p>
                <button
                    type="button"
                    onClick={addNew}
                    data-testid="interlocutor-add"
                    className="inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                >
                    <Plus strokeWidth={1.2} size={12} />
                    Ajouter
                </button>
            </div>

            {list.length === 0 ? (
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light py-6 text-center">
                    Aucun interlocuteur enregistré.
                </p>
            ) : (
                <ul className="space-y-2 mb-7">
                    {list.map((it) => (
                        <InterlocutorRow
                            key={it.id}
                            it={it}
                            expanded={expandedId === it.id}
                            onExpand={() =>
                                setExpandedId(expandedId === it.id ? null : it.id)
                            }
                            onChange={(patch) => updateLocal(it.id, patch)}
                            onRemove={() => removeLocal(it.id)}
                            partnerId={partner.id}
                            onPartnerRefresh={(p) => {
                                setList(p.interlocutors || []);
                                onUpdated?.(p);
                            }}
                        />
                    ))}
                </ul>
            )}

            {err && (
                <div className="mb-4 text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                    {err}
                </div>
            )}

            <div className="flex justify-end gap-4">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                >
                    Fermer
                </button>
                <button
                    type="button"
                    onClick={saveAll}
                    data-testid="interlocutors-save"
                    disabled={busy}
                    className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    {busy ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>
        </div>
    );
}

function InterlocutorRow({
    it,
    expanded,
    onExpand,
    onChange,
    onRemove,
    partnerId,
    onPartnerRefresh,
}) {
    const fullName = `${it.first_name || ""} ${it.last_name || ""}`.trim();
    const [impact, setImpact] = useState(null);
    const isSaved = !String(it.id).startsWith("new-");

    useEffect(() => {
        if (expanded && isSaved) {
            cockpit
                .get(`/network/interlocutors/${it.id}/impact`)
                .then((r) => setImpact(r.data))
                .catch(() => setImpact(null));
        }
    }, [expanded, isSaved, it.id]);

    return (
        <li
            data-testid={`interlocutor-${it.id}`}
            className="border border-white/[0.06]"
        >
            <button
                type="button"
                onClick={onExpand}
                className="w-full grid grid-cols-[1fr_auto_auto] items-center gap-5 py-3.5 px-4 hover:bg-white/[0.02] transition-colors duration-500 text-left"
            >
                <div className="min-w-0">
                    <div
                        className="font-serif text-[16px] text-[#f4ecd8] leading-tight truncate"
                        style={{ fontWeight: 400 }}
                    >
                        {fullName || "Nouvel interlocuteur"}
                    </div>
                    <div className="mt-0.5 text-[11.5px] text-[#f4ecd8]/45 truncate">
                        {it.role_title || "—"}
                        {it.location ? ` · ${it.location}` : ""}
                    </div>
                </div>
                <span className="text-[10px] tracking-[0.24em] uppercase text-[#d4af37]/70 hidden sm:inline">
                    {ROLES.find((r) => r.v === it.role)?.l || "—"}
                </span>
                <span className="text-[11px] text-[#d4af37]/70">
                    {it.trust_level || 3}/5
                </span>
            </button>

            {expanded && (
                <div className="px-4 pb-5 pt-2 space-y-5 border-t border-white/[0.04]">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <MicroField
                            label="Prénom"
                            value={it.first_name}
                            onChange={(v) => onChange({ first_name: v })}
                        />
                        <MicroField
                            label="Nom"
                            value={it.last_name}
                            onChange={(v) => onChange({ last_name: v })}
                        />
                        <MicroField
                            label="Fonction"
                            value={it.role_title}
                            onChange={(v) => onChange({ role_title: v })}
                        />
                        <MicroField
                            label="Société"
                            value={it.company}
                            onChange={(v) => onChange({ company: v })}
                        />
                        <MicroField
                            label="Email"
                            value={it.email}
                            onChange={(v) => onChange({ email: v })}
                        />
                        <MicroField
                            label="Téléphone"
                            value={it.phone}
                            onChange={(v) => onChange({ phone: v })}
                        />
                        <MicroField
                            label="Localisation"
                            value={it.location}
                            onChange={(v) => onChange({ location: v })}
                        />
                        <div>
                            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/75 mb-2">
                                Rôle
                            </label>
                            <select
                                value={it.role || ""}
                                onChange={(e) =>
                                    onChange({ role: e.target.value })
                                }
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13.5px] font-serif text-[#f4ecd8]"
                            >
                                {ROLES.map((r) => (
                                    <option
                                        key={r.v}
                                        value={r.v}
                                        className="bg-[#0b1220]"
                                    >
                                        {r.l}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/75 mb-2">
                                Confiance (1-5)
                            </label>
                            <input
                                type="number"
                                min={1}
                                max={5}
                                value={it.trust_level || 3}
                                onChange={(e) =>
                                    onChange({
                                        trust_level: Number(e.target.value) || 3,
                                    })
                                }
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13.5px] font-serif text-[#f4ecd8]"
                            />
                        </div>
                        <MicroField
                            label="Dernier échange"
                            value={it.last_interaction}
                            onChange={(v) => onChange({ last_interaction: v })}
                            placeholder="2026-01-15"
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/75 mb-2">
                            Historique libre
                        </label>
                        <textarea
                            value={it.history || ""}
                            onChange={(e) => onChange({ history: e.target.value })}
                            rows={3}
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[13.5px] font-serif text-[#f4ecd8] resize-y"
                        />
                    </div>

                    {/* IMPACT BUSINESS — visible only once persisted */}
                    {isSaved && impact && (
                        <ImpactBlock impact={impact} />
                    )}

                    {/* INTERACTIONS */}
                    {isSaved && (
                        <InteractionsBlock
                            partnerId={partnerId}
                            interlocutorId={it.id}
                            interactions={it.interactions || []}
                            onRefresh={onPartnerRefresh}
                        />
                    )}

                    <div className="flex justify-between pt-2">
                        <button
                            type="button"
                            onClick={onRemove}
                            data-testid={`interlocutor-delete-${it.id}`}
                            className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#e89090]/80 hover:text-[#e89090] transition-colors duration-500"
                        >
                            <Trash2 strokeWidth={1.2} size={11} />
                            Retirer
                        </button>
                        <span className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/35 italic">
                            {isSaved
                                ? "Pensez à enregistrer si modifié"
                                : "Cliquez sur Enregistrer pour finaliser"}
                        </span>
                    </div>
                </div>
            )}
        </li>
    );
}

function MicroField({ label, value, onChange, placeholder }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/75 mb-2">
                {label}
            </label>
            <input
                type="text"
                value={value || ""}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13.5px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
            />
        </div>
    );
}

function ImpactBlock({ impact }) {
    const last = impact.last_contribution
        ? new Date(impact.last_contribution).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
          })
        : "—";
    return (
        <div
            data-testid="impact-block"
            className="bg-white/[0.02] border border-white/[0.06] p-5"
        >
            <div className="overline text-[#d4af37]/70 mb-4">
                <TrendingUp
                    strokeWidth={1}
                    size={12}
                    className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                />
                Contribution réseau
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-white/[0.06]">
                <MiniStat
                    label="Apportés"
                    value={(impact.dossiers_introduced || []).length}
                />
                <MiniStat
                    label="Accompagnés"
                    value={(impact.dossiers_mobilized || []).length}
                />
                <MiniStat label="Introductions" value={impact.introductions_count} />
                <MiniStat label="Mises en relation" value={impact.referrals_count} />
                <MiniStat label="CA indirect" value={fmtMoney(impact.revenue_indirect)} />
            </div>
            <div className="mt-4 flex items-center justify-between text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/45">
                <span>Dernière contribution</span>
                <span className="text-[#d4af37]/75">{last}</span>
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="bg-[#070d18] p-4">
            <div className="text-[9.5px] tracking-[0.26em] uppercase text-[#f4ecd8]/45 mb-1.5">
                {label}
            </div>
            <div
                className="font-serif text-[20px] text-[#f4ecd8] leading-none"
                style={{ fontWeight: 300 }}
            >
                {value ?? 0}
            </div>
        </div>
    );
}

function InteractionsBlock({
    partnerId,
    interlocutorId,
    interactions,
    onRefresh,
}) {
    const [kind, setKind] = useState("appel");
    const [date, setDate] = useState(() =>
        new Date().toISOString().slice(0, 10),
    );
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const add = async () => {
        if (!kind) return;
        setBusy(true);
        try {
            const { data } = await cockpit.post(
                `/partners/${partnerId}/interlocutors/${interlocutorId}/interactions`,
                { kind, date, note },
            );
            setNote("");
            onRefresh?.(data);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (aid) => {
        if (!window.confirm("Retirer cette interaction ?")) return;
        const { data } = await cockpit.delete(
            `/partners/${partnerId}/interlocutors/${interlocutorId}/interactions/${aid}`,
        );
        onRefresh?.(data);
    };

    return (
        <div data-testid="interactions-block">
            <div className="overline text-[#d4af37]/70 mb-4">
                <Activity
                    strokeWidth={1}
                    size={12}
                    className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                />
                Journal d'interactions
            </div>
            <div className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-3 mb-4 items-end">
                <div>
                    <label className="block text-[9.5px] tracking-[0.28em] uppercase text-[#d4af37]/65 mb-1.5">
                        Type
                    </label>
                    <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        data-testid="interaction-kind"
                        className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif text-[#f4ecd8]"
                    >
                        {Object.entries(INTERACTION_LABELS).map(([v, l]) => (
                            <option key={v} value={v} className="bg-[#0b1220]">
                                {l}
                            </option>
                        ))}
                    </select>
                </div>
                <MicroField label="Date" value={date} onChange={setDate} />
                <MicroField
                    label="Note"
                    value={note}
                    onChange={setNote}
                    placeholder="Échange sur la structuration patrimoniale…"
                />
                <button
                    type="button"
                    onClick={add}
                    disabled={busy}
                    data-testid="interaction-add"
                    className="px-4 py-2 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    Ajouter
                </button>
            </div>
            <ul className="space-y-2">
                {interactions.map((a) => {
                    const Icon = INTERACTION_ICONS[a.kind] || Activity;
                    return (
                        <li
                            key={a.id}
                            data-testid={`interaction-${a.id}`}
                            className="group flex items-start gap-3 py-2 border-b border-white/[0.04] last:border-b-0"
                        >
                            <Icon
                                strokeWidth={1.1}
                                size={12}
                                className="text-[#d4af37] mt-1 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 text-[10px] tracking-[0.24em] uppercase">
                                    <span className="text-[#d4af37]/85">
                                        {INTERACTION_LABELS[a.kind] || a.kind}
                                    </span>
                                    <span className="text-[#f4ecd8]/45">
                                        {a.date}
                                    </span>
                                </div>
                                {a.note && (
                                    <p className="mt-1 text-[12.5px] font-serif text-[#f4ecd8]/80 leading-relaxed">
                                        {a.note}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(a.id)}
                                className="opacity-0 group-hover:opacity-100 text-[#f4ecd8]/30 hover:text-[#e89090] transition mt-1"
                            >
                                <X strokeWidth={1.3} size={12} />
                            </button>
                        </li>
                    );
                })}
                {interactions.length === 0 && (
                    <li className="text-[11.5px] text-[#f4ecd8]/35 italic font-light py-1">
                        Aucune interaction enregistrée.
                    </li>
                )}
            </ul>
        </div>
    );
}

// Briefcase imported but kept here to satisfy lint if not directly used inline.
// eslint-disable-next-line no-unused-expressions
Briefcase;
// useMemo placeholder — reserved for future memoisation.
// eslint-disable-next-line no-unused-expressions
useMemo;
